import React, { useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '@/contexts/AuthContext';

// Custom high-end SVGs for premium clinical aesthetic (No emojis)
const SvgCheck = () => (
  <svg className="w-4 h-4 text-[#2f6f3e]" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const SvgClose = () => (
  <svg className="w-4 h-4 text-[#6d6a63]" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const SvgDoc = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const SvgSearch = () => (
  <svg className="w-4 h-4 text-[#6d6a63]" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const SvgCard = () => (
  <svg className="w-5 h-5 text-[#0f3e17]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
  </svg>
);

const SvgShield = () => (
  <svg className="w-4 h-4 text-[#2f6f3e]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

export default function GenomicsPage() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [counselors, setCounselors] = useState([]);
  const [orders, setOrders] = useState([]);

  // Booking wizard state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [wizardStep, setStep] = useState(1); // 1: Schedule, 2: HIPAA Consent, 3: Stripe Checkout
  const [address, setAddress] = useState({ line1: '', city: '', state: '', pincode: '' }); // pincode used as ZIP
  const [slot, setSlot] = useState({ date: '', time_window: '08:00 AM - 11:00 AM', type: 'HOME_COLLECTION' });
  const [consentOptIn, setConsentOptIn] = useState(false);
  const [payMethod, setPayMethod] = useState('STRIPE');
  const [isSimulatingPayment, setIsSimulatingPayment] = useState(false);
  const [paymentDone, setPaymentDone] = useState(false);

  // Ingestion and parsing state
  const [selectedOrderForUpload, setSelectedOrderForUpload] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [parsedVariants, setParsedVariants] = useState([]);
  const [hasParsed, setHasParsed] = useState(false);

  // Annotations state
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [annotation, setAnnotation] = useState(null);
  const [isBookingCounselor, setIsBookingCounselor] = useState(null); // holds counselor obj
  const [bookingDate, setBookingDate] = useState('');
  const [bookingSuccess, setBookingSuccess] = useState(false);

  // Status message alerts
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    fetchCatalog();
    fetchCounselors();
    fetchOrders();
  }, []);

  const fetchCatalog = async () => {
    try {
      const res = await api.get('/api/genomics/catalog');
      setCatalog(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCounselors = async () => {
    try {
      const res = await api.get('/api/genomics/counselors');
      setCounselors(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchOrders = async () => {
    try {
      const res = await api.get('/api/genomics/orders');
      setOrders(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const showToast = (message, type = 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 5000);
  };

  const handleStartBooking = (product) => {
    setSelectedProduct(product);
    setStep(1);
    setConsentOptIn(false);
    setPaymentDone(false);
    setIsSimulatingPayment(false);
  };

  const handleNextStep = () => {
    if (wizardStep === 1) {
      if (!address.line1 || !address.city || !address.state || !address.pincode || !slot.date) {
        showToast('Please fill out all address and collection date fields.', 'error');
        return;
      }
      setStep(2);
    } else if (wizardStep === 2) {
      if (!consentOptIn) {
        showToast('You must authorize genetic profiling consent under HIPAA & GINA regulations.', 'error');
        return;
      }
      setStep(3);
    }
  };

  const handlePaymentSimulate = () => {
    if (payMethod === 'PAY_AT_COLLECTION') {
      setPaymentDone(true);
      return;
    }
    setIsSimulatingPayment(true);
    setTimeout(() => {
      setIsSimulatingPayment(false);
      setPaymentDone(true);
      showToast('Stripe Mock Transaction authorized successfully!');
    }, 2000);
  };

  const handleConfirmOrder = async () => {
    try {
      const payload = {
        product_id: selectedProduct.id,
        consent: {
          consent_version: 'v2.1',
          has_opted_in: consentOptIn,
        },
        collection_slot: slot,
        collection_address: address,
        payment_method: payMethod,
      };
      await api.post('/api/genomics/orders', payload);
      showToast('Laboratory diagnostic panel ordered and scheduled!');
      setSelectedProduct(null);
      fetchOrders();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Order placement failed.', 'error');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/api/genomics/reports/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setParsedVariants(res.data.variants || []);
      setHasParsed(true);
      showToast('Genomic report parsed! Please verify extracted variants.');
    } catch (err) {
      showToast('PDF parsing failed. Please input mutations manually.', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleVerifyAndSave = async () => {
    try {
      await api.post(`/api/genomics/orders/${selectedOrderForUpload.id}/confirm-variants`, {
        variants: parsedVariants,
      });
      showToast('Biomarkers verified and linked to clinical trial matching profile! Clinical trial recommendations updated.');
      setSelectedOrderForUpload(null);
      setHasParsed(false);
      fetchOrders();
    } catch (err) {
      showToast('Verification failed.', 'error');
    }
  };

  const handleFetchAnnotation = async (gene, variant) => {
    try {
      const res = await api.get(`/api/genomics/variants/${gene}/${variant}/annotation`);
      setAnnotation(res.data);
      setSelectedVariant({ gene, variant });
    } catch (err) {
      showToast('Could not fetch variant annotations.', 'error');
    }
  };

  const handleBookCounselor = async () => {
    if (!bookingDate) {
      showToast('Please select a booking date and time.', 'error');
      return;
    }
    try {
      await api.post(`/api/genomics/counselors/${isBookingCounselor.id}/book`, {
        counselor_id: isBookingCounselor.id,
        date_time: bookingDate,
      });
      setBookingSuccess(true);
      setTimeout(() => {
        setIsBookingCounselor(null);
        setBookingSuccess(false);
        setBookingDate('');
      }, 3000);
    } catch (err) {
      showToast('Booking failed.', 'error');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 text-[#161616] font-sans">
      {/* Alert toast */}
      {alert && (
        <div className={`fixed top-5 right-5 z-50 p-4 rounded-xl shadow-2xl transition-all duration-300 ${alert.type === 'error' ? 'bg-[#b42318] text-white' : 'bg-[#2f6f3e] text-white'} text-xs font-bold`}>
          <div className="flex items-center gap-3">
            <span>{alert.message}</span>
          </div>
        </div>
      )}

      {/* Header section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-[#e6e2d8] pb-6 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-[#161616] flex items-center gap-2.5 font-serif">
            Molecular Diagnostics & Genetic Testing
          </h1>
          <p className="text-[#6d6a63] mt-2 text-sm max-w-2xl leading-relaxed">
            Order certified laboratory panels, ingest clinical sequencing reports, and trace ClinVar therapeutic overlays linked automatically to your clinical trial match profile.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Products Catalog & Timeline tracking */}
        <div className="lg:col-span-2 space-y-8">
          
          {/* Test Catalog */}
          <div className="bg-white rounded-2xl border border-[#e6e2d8] p-6 shadow-sm">
            <h2 className="text-sm font-extrabold text-[#161616] uppercase tracking-wider mb-5 pb-3 border-b border-[#e6e2d8]/60">
              Diagnostics Testing Catalog
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {catalog.map((product) => (
                <div key={product.id} className="flex flex-col justify-between border border-[#e6e2d8] rounded-2xl p-5 hover:border-[#0f3e17]/30 transition-all shadow-sm bg-[#fdfdfc]">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center gap-2">
                      <span className={`px-2.5 py-1 rounded-lg text-[10px] font-extrabold uppercase tracking-wider ${
                        product.category === 'Somatic' ? 'bg-[#0f3e17]/5 text-[#0f3e17] border border-[#0f3e17]/10' : product.category === 'Germline' ? 'bg-[#c9a227]/5 text-[#c9a227] border border-[#c9a227]/10' : 'bg-[#2f6f3e]/5 text-[#2f6f3e] border border-[#2f6f3e]/10'
                      }`}>
                        {product.category}
                      </span>
                      <span className="text-lg font-bold text-[#0f3e17]">${(product.price_usd || 1500).toLocaleString()}</span>
                    </div>
                    <h3 className="font-extrabold text-[#161616] text-base leading-snug font-serif">{product.name}</h3>
                    <p className="text-[#6d6a63] text-xs font-semibold leading-relaxed">
                      {product.description_en}
                    </p>
                    <div className="mt-4 border-t border-[#e6e2d8]/60 pt-3.5 text-[11px] text-[#6d6a63] space-y-1.5 font-semibold">
                      <div><strong className="text-[#161616]">Sample Needed:</strong> {product.sample_type}</div>
                      <div><strong className="text-[#161616]">Turnaround:</strong> {product.turnaround_days} Days</div>
                    </div>
                  </div>
                  <button onClick={() => handleStartBooking(product)} className="w-full mt-5 bg-[#0f3e17] hover:bg-[#1a4a21] text-white font-bold py-3 px-4 rounded-xl transition-all shadow-sm border border-[#0f3e17] uppercase text-xs tracking-wider">
                    Order Panel
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Active Orders Timeline */}
          <div className="bg-white rounded-2xl border border-[#e6e2d8] p-6 shadow-sm">
            <h2 className="text-sm font-extrabold text-[#161616] uppercase tracking-wider mb-5 pb-3 border-b border-[#e6e2d8]/60">
              Active Testing Timelines
            </h2>
            {orders.length === 0 ? (
              <p className="text-[#6d6a63] text-xs font-semibold italic">No active genomic orders tracked yet.</p>
            ) : (
              <div className="space-y-6">
                {orders.map((order) => {
                  const prod = catalog.find(p => p.id === order.product_id);
                  return (
                    <div key={order.id} className="border border-[#e6e2d8] rounded-2xl p-5 bg-[#fdfdfc] space-y-4">
                      <div className="flex justify-between items-start flex-wrap gap-2">
                        <div>
                          <h3 className="font-extrabold text-[#161616] text-sm font-serif">{prod?.name || order.product_id}</h3>
                          <p className="text-[10px] font-mono font-bold text-[#6d6a63] mt-1">Order ID: {order.id} • Lab Ref: {order.lab_order_id}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-2.5 py-1 rounded-lg bg-[#c9a227]/10 text-[#c9a227] border border-[#c9a227]/20 text-[10px] font-extrabold uppercase tracking-wider">
                            {order.status.replace(/_/g, ' ')}
                          </span>
                          {order.status === 'SAMPLE_COLLECTED' && (
                            <button onClick={() => setSelectedOrderForUpload(order)} className="px-3 py-1.5 bg-[#0f3e17] hover:bg-[#1a4a21] text-white rounded-lg text-xs font-bold shadow-sm flex items-center gap-1.5 border border-[#0f3e17] uppercase tracking-wider">
                              <SvgDoc />
                              <span>Upload Report</span>
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Animated timeline tracks */}
                      <div className="grid grid-cols-5 gap-2 text-center text-[10px] text-[#6d6a63] font-extrabold uppercase tracking-wide">
                        <div className="space-y-1">
                          <div className="h-1.5 rounded bg-[#2f6f3e]"></div>
                          <span className="text-[#2f6f3e]">Booked</span>
                        </div>
                        <div className="space-y-1">
                          <div className={`h-1.5 rounded ${order.status !== 'SAMPLE_COLLECTION_PENDING' ? 'bg-[#2f6f3e]' : 'bg-[#e6e2d8]'}`}></div>
                          <span>Collected</span>
                        </div>
                        <div className="space-y-1">
                          <div className={`h-1.5 rounded ${['LAB_PROCESSING', 'REPORT_READY'].includes(order.status) ? 'bg-[#2f6f3e]' : 'bg-[#e6e2d8]'}`}></div>
                          <span>Received</span>
                        </div>
                        <div className="space-y-1">
                          <div className={`h-1.5 rounded ${['LAB_PROCESSING', 'REPORT_READY'].includes(order.status) ? 'bg-[#2f6f3e]' : 'bg-[#e6e2d8]'}`}></div>
                          <span>Sequencing</span>
                        </div>
                        <div className="space-y-1">
                          <div className={`h-1.5 rounded ${order.status === 'REPORT_READY' ? 'bg-[#2f6f3e]' : 'bg-[#e6e2d8]'}`}></div>
                          <span className={order.status === 'REPORT_READY' ? 'text-[#2f6f3e] font-extrabold' : ''}>Report Ready</span>
                        </div>
                      </div>

                      {/* Confirmed Variants Listing */}
                      {order.variants_extracted?.length > 0 && (
                        <div className="mt-4 pt-3.5 border-t border-[#e6e2d8]">
                          <h4 className="text-[10px] font-extrabold text-[#6d6a63] uppercase tracking-wider mb-2.5">Extracted actionable variants:</h4>
                          <div className="flex flex-wrap gap-2">
                            {order.variants_extracted.map((v, idx) => (
                              <button key={idx} onClick={() => handleFetchAnnotation(v.gene, v.variant_name)} className="px-3 py-1.5 bg-[#0f3e17]/5 hover:bg-[#0f3e17]/10 text-[#0f3e17] text-xs font-bold rounded-xl border border-[#0f3e17]/10 flex items-center gap-1.5">
                                <SvgSearch />
                                <span>{v.gene} {v.variant_name}</span>
                                {v.vaf_percentage && <span className="opacity-60">({v.vaf_percentage}%)</span>}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right Column: In-context genomic annotations and Genetic counselors search */}
        <div className="space-y-8">
          {/* Diagnostic interpretation annotations card */}
          <div className="bg-white rounded-2xl border border-[#e6e2d8] p-6 shadow-sm">
            <h2 className="text-sm font-extrabold text-[#161616] uppercase tracking-wider mb-5 pb-3 border-b border-[#e6e2d8]/60">
              Molecular Interpretation Board
            </h2>
            {!selectedVariant ? (
              <div className="text-center py-10 text-[#6d6a63] space-y-4">
                <div className="flex justify-center">
                  <SvgSearch />
                </div>
                <p className="text-xs font-semibold leading-relaxed max-w-xs mx-auto">
                  Select a confirmed biomarker from your active timelines or scan a genomic report to query ClinVar, CIViC & FDA therapeutic indications.
                </p>
              </div>
            ) : (
              <div className="space-y-5">
                <div className="flex justify-between items-start gap-2 border-b border-[#e6e2d8] pb-4">
                  <div>
                    <h3 className="text-2xl font-extrabold text-[#161616] font-serif">{selectedVariant.gene} <span className="text-[#0f3e17]">{selectedVariant.variant}</span></h3>
                    <p className="text-[10px] text-[#6d6a63] mt-1 font-bold uppercase tracking-wider">Classification: <span className="text-[#161616]">{annotation?.clinvar_classification}</span></p>
                  </div>
                  <span className="px-2.5 py-1 bg-[#0f3e17]/5 border border-[#0f3e17]/10 text-[#0f3e17] rounded-lg text-[10px] font-extrabold tracking-wider uppercase">
                    {annotation?.evidence_tier || 'Tier I'}
                  </span>
                </div>

                <div className="space-y-4 text-xs font-semibold">
                  <div>
                    <strong className="text-[10px] font-extrabold text-[#6d6a63] uppercase tracking-wider block">Clinical Actionability & Target Overview:</strong>
                    <p className="text-[#161616] mt-1.5 leading-relaxed font-normal">{annotation?.description}</p>
                  </div>

                  <div className="space-y-1.5">
                    <strong className="text-[10px] font-extrabold text-[#6d6a63] uppercase tracking-wider block">FDA Companion Diagnostics & Guidelines:</strong>
                    {annotation?.target_therapies?.length > 0 ? (
                      <div className="flex flex-wrap gap-2 mt-1.5">
                        {annotation.target_therapies.map((t, idx) => (
                          <span key={idx} className="px-2.5 py-1 bg-[#2f6f3e]/5 text-[#2f6f3e] text-[10px] font-bold rounded-lg border border-[#2f6f3e]/10 uppercase tracking-wide">
                            {t}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[#6d6a63] mt-1 text-xs italic font-normal">No specific companion targeted inhibitors registered.</p>
                    )}
                  </div>

                  <div className="bg-[#ece8df]/15 rounded-xl p-3 border border-[#e6e2d8] text-[10px] text-[#6d6a63] space-y-1 font-mono font-bold uppercase">
                    <div>CIViC Support: {annotation?.civic_evidence_level || 'Level A'}</div>
                    <div>Registry: {annotation?.source || 'ClinVar / CIViC'}</div>
                  </div>
                </div>

                {/* Genetic Counseling Advice Banner */}
                {annotation?.clinvar_classification?.includes('Germline') && (
                  <div className="bg-[#c9a227]/5 rounded-xl p-4 border border-[#c9a227]/20 space-y-2">
                    <h4 className="font-extrabold text-[#c9a227] text-xs uppercase tracking-wider flex items-center gap-1.5">
                      <SvgShield />
                      <span>Hereditary / Germline Variant Guidance</span>
                    </h4>
                    <p className="text-xs text-[#6d6a63] leading-relaxed font-semibold">
                      Inherited germline alleles may pose familial risk predispositions. Standard medical guidelines recommend scheduling a consultation with a clinical geneticist.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Genetic Counselors directory */}
          <div className="bg-white rounded-2xl border border-[#e6e2d8] p-6 shadow-sm">
            <h2 className="text-sm font-extrabold text-[#161616] uppercase tracking-wider mb-5 pb-3 border-b border-[#e6e2d8]/60">
              Oncology Counselors Directory
            </h2>
            <div className="space-y-4">
              {counselors.map((c) => (
                <div key={c.id} className="border border-[#e6e2d8] rounded-2xl p-4 bg-[#fdfdfc] flex flex-col justify-between gap-3 hover:border-[#0f3e17]/30 transition-all">
                  <div className="space-y-1.5">
                    <h3 className="font-extrabold text-[#161616] text-sm font-serif">{c.name}</h3>
                    <p className="text-xs text-[#0f3e17] font-bold uppercase tracking-wider">{c.specialty}</p>
                    <p className="text-[11px] text-[#6d6a63] font-semibold">{c.hospital_affiliation}</p>
                  </div>
                  <button onClick={() => setIsBookingCounselor(c)} className="w-full text-xs font-bold py-2.5 px-3 bg-[#0f3e17] hover:bg-[#1a4a21] text-white rounded-xl shadow-sm border border-[#0f3e17] uppercase tracking-wider">
                    Book Session (${c.consultation_fee_usd || 150})
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 1. Booking Wizard Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 z-50 bg-[#161616]/65 backdrop-blur-sm flex justify-center items-center p-4">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden border border-[#e6e2d8] animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center bg-[#fdfdfc] px-6 py-4 border-b border-[#e6e2d8]">
              <div>
                <h3 className="font-extrabold text-[#161616] text-lg font-serif">Diagnostics Scheduler</h3>
                <p className="text-xs text-[#6d6a63] font-semibold mt-0.5">Order: {selectedProduct.name}</p>
              </div>
              <button onClick={() => setSelectedProduct(null)} className="p-1.5 border border-[#e6e2d8] hover:border-[#6d6a63] rounded-full bg-white transition">
                <SvgClose />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Stepper tracker */}
              <div className="flex justify-between items-center text-[10px] text-[#6d6a63] font-extrabold uppercase tracking-wider border-b border-[#e6e2d8]/60 pb-3">
                <span className={`pb-1 ${wizardStep >= 1 ? 'text-[#0f3e17] border-b-2 border-[#0f3e17]' : ''}`}>1. Location & Slot</span>
                <span className={`pb-1 ${wizardStep >= 2 ? 'text-[#0f3e17] border-b-2 border-[#0f3e17]' : ''}`}>2. HIPAA Authorization</span>
                <span className={`pb-1 ${wizardStep >= 3 ? 'text-[#0f3e17] border-b-2 border-[#0f3e17]' : ''}`}>3. Stripe Verification</span>
              </div>

              {/* Wizard Step 1: Scheduling Details */}
              {wizardStep === 1 && (
                <div className="space-y-4 text-xs font-semibold">
                  <div className="space-y-2">
                    <label className="block text-[10px] font-extrabold text-[#6d6a63] uppercase tracking-wider">Collection Mode</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button onClick={() => setSlot({ ...slot, type: 'HOME_COLLECTION' })} className={`py-3.5 rounded-xl text-xs font-extrabold transition-all border flex items-center justify-center gap-1.5 ${slot.type === 'HOME_COLLECTION' ? 'bg-[#0f3e17]/5 text-[#0f3e17] border-[#0f3e17]' : 'bg-white border-[#e6e2d8] text-[#6d6a63]'}`}>
                        🏡 At-Home Kit / Nurse
                      </button>
                      <button onClick={() => setSlot({ ...slot, type: 'LAB_VISIT' })} className={`py-3.5 rounded-xl text-xs font-extrabold transition-all border flex items-center justify-center gap-1.5 ${slot.type === 'LAB_VISIT' ? 'bg-[#0f3e17]/5 text-[#0f3e17] border-[#0f3e17]' : 'bg-white border-[#e6e2d8] text-[#6d6a63]'}`}>
                        🏥 Local US Partner Lab
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-extrabold text-[#6d6a63] uppercase tracking-wider">Preferred Date</label>
                      <input type="date" value={slot.date} onChange={(e) => setSlot({ ...slot, date: e.target.value })} className="w-full text-xs p-2.5 rounded-xl border border-[#e6e2d8] font-semibold" min={new Date().toISOString().split('T')[0]} />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-extrabold text-[#6d6a63] uppercase tracking-wider">Time Window</label>
                      <select value={slot.time_window} onChange={(e) => setSlot({ ...slot, time_window: e.target.value })} className="w-full text-xs p-2.5 rounded-xl border border-[#e6e2d8] bg-white font-semibold">
                        <option>08:00 AM - 11:00 AM</option>
                        <option>11:00 AM - 02:00 PM</option>
                        <option>02:00 PM - 05:00 PM</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2 pt-2">
                    <label className="block text-[10px] font-extrabold text-[#6d6a63] uppercase tracking-wider">US Delivery / Billing Address</label>
                    <input type="text" placeholder="Flat / Suite No, Street Name" value={address.line1} onChange={(e) => setAddress({ ...address, line1: e.target.value })} className="w-full text-xs p-2.5 rounded-xl border border-[#e6e2d8] font-semibold" />
                    <div className="grid grid-cols-3 gap-2">
                      <input type="text" placeholder="City" value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} className="text-xs p-2.5 rounded-xl border border-[#e6e2d8] font-semibold" />
                      <input type="text" placeholder="State" value={address.state} onChange={(e) => setAddress({ ...address, state: e.target.value })} className="text-xs p-2.5 rounded-xl border border-[#e6e2d8] font-semibold" />
                      <input type="text" placeholder="ZIP Code" value={address.pincode} onChange={(e) => setAddress({ ...address, pincode: e.target.value })} className="text-xs p-2.5 rounded-xl border border-[#e6e2d8] font-semibold" />
                    </div>
                  </div>
                </div>
              )}

              {/* Wizard Step 2: HIPAA Consent */}
              {wizardStep === 2 && (
                <div className="space-y-4 text-xs font-semibold">
                  <div className="bg-[#ece8df]/25 rounded-2xl p-5 border border-[#e6e2d8] text-xs text-[#6d6a63] space-y-3 leading-relaxed font-medium">
                    <h4 className="font-extrabold text-[#161616] text-sm flex items-center gap-1.5 font-serif">
                      <SvgShield />
                      <span>HIPAA & GINA Electronic Consent Authorization</span>
                    </h4>
                    <p>In adherence to the **Health Insurance Portability and Accountability Act (HIPAA)** and the **Genetic Information Nondiscrimination Act (GINA)**:</p>
                    <ul className="list-disc pl-4 space-y-1.5 text-[11px]">
                      <li>We encrypt and restrict molecular diagnostic sequencing datasets solely for clinical matching purposes.</li>
                      <li>You retain full rights to request complete data erasure or download your sequencing records at any time.</li>
                      <li>Genetic profiling details are securely isolated and will never be shared with health insurance providers or unauthorized third parties.</li>
                    </ul>
                  </div>

                  <label className="flex items-start gap-3 p-4 border border-[#e6e2d8] rounded-xl hover:bg-[#ece8df]/15 cursor-pointer">
                    <input type="checkbox" checked={consentOptIn} onChange={(e) => setConsentOptIn(e.target.checked)} className="mt-1 accent-[#0f3e17]" />
                    <span className="text-xs font-semibold text-[#161616] leading-normal">
                      I explicitly, freely, and unconditionally consent to the collection, secure storage, and clinical bio-analysis of my genetic and molecular health data under HIPAA/GINA guidelines.
                    </span>
                  </label>
                </div>
              )}

              {/* Wizard Step 3: Simulation payment checkouts */}
              {wizardStep === 3 && (
                <div className="space-y-5 text-xs font-semibold">
                  <div className="flex justify-between items-center p-4 bg-[#0f3e17]/5 border border-[#0f3e17]/10 rounded-xl">
                    <span className="font-extrabold text-[#6d6a63] text-sm uppercase tracking-wider">Grand Total (USD):</span>
                    <span className="text-xl font-bold text-[#0f3e17]">${(selectedProduct.price_usd || 1500).toLocaleString()}</span>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-[10px] font-extrabold text-[#6d6a63] uppercase tracking-wider">Secure Payment Gateway</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button onClick={() => setPayMethod('STRIPE')} className={`py-3.5 rounded-xl text-xs font-extrabold transition-all border flex items-center justify-center gap-1.5 ${payMethod === 'STRIPE' ? 'bg-[#0f3e17]/5 text-[#0f3e17] border-[#0f3e17]' : 'bg-white border-[#e6e2d8] text-[#6d6a63]'}`}>
                        <SvgCard />
                        <span>Stripe Secure Credit Card</span>
                      </button>
                      <button onClick={() => setPayMethod('PAY_AT_COLLECTION')} className={`py-3.5 rounded-xl text-xs font-extrabold transition-all border flex items-center justify-center gap-1.5 ${payMethod === 'PAY_AT_COLLECTION' ? 'bg-[#0f3e17]/5 text-[#0f3e17] border-[#0f3e17]' : 'bg-white border-[#e6e2d8] text-[#6d6a63]'}`}>
                        💵 Pay at Collection / Kit Delivery
                      </button>
                    </div>
                  </div>

                  {/* payment animation displays */}
                  {isSimulatingPayment ? (
                    <div className="text-center py-6 text-[#6d6a63] space-y-2">
                      <span className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0f3e17] mx-auto block"></span>
                      <p className="font-bold uppercase tracking-wider text-[10px]">Authorizing Stripe Secure Handshake...</p>
                    </div>
                  ) : paymentDone ? (
                    <div className="bg-[#2f6f3e]/10 border border-[#2f6f3e]/20 text-[#2f6f3e] p-4 rounded-xl text-xs font-semibold flex items-center gap-2">
                      <SvgCheck />
                      <span>Card processed and approved! Secure transaction logged. Please click "Confirm Diagnostic Booking".</span>
                    </div>
                  ) : (
                    <button onClick={handlePaymentSimulate} className="w-full py-3.5 bg-[#0f3e17] hover:bg-[#1a4a21] text-white font-bold rounded-xl transition-all shadow-sm border border-[#0f3e17] uppercase tracking-wider text-xs">
                      {payMethod === 'STRIPE' ? 'Simulate Stripe Checkout Authorization' : 'Authorize pay-on-collection booking'}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Bottom Actions footer */}
            <div className="bg-[#fdfdfc] px-6 py-4 border-t border-[#e6e2d8] flex justify-between gap-3 flex-shrink-0">
              {wizardStep > 1 ? (
                <button onClick={() => setStep(wizardStep - 1)} className="py-2.5 px-4 border border-[#e6e2d8] hover:bg-[#ece8df]/40 text-xs font-bold text-[#6d6a63] rounded-xl transition">
                  Back
                </button>
              ) : (
                <div />
              )}

              {wizardStep < 3 ? (
                <button onClick={handleNextStep} className="py-2.5 px-5 bg-[#0f3e17] hover:bg-[#1a4a21] text-white text-xs font-bold rounded-xl transition border border-[#0f3e17] uppercase tracking-wider">
                  Continue
                </button>
              ) : (
                <button onClick={handleConfirmOrder} disabled={!paymentDone} className="py-2.5 px-5 bg-[#0f3e17] hover:bg-[#1a4a21] text-white text-xs font-bold rounded-xl transition disabled:opacity-50 border border-[#0f3e17] uppercase tracking-wider">
                  Confirm Diagnostic Booking
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 2. Upload PDF Report Modal */}
      {selectedOrderForUpload && (
        <div className="fixed inset-0 z-50 bg-[#161616]/65 backdrop-blur-sm flex justify-center items-center p-4">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl p-6 border border-[#e6e2d8] space-y-5 animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center border-b border-[#e6e2d8] pb-3 text-xs font-semibold">
              <div>
                <h3 className="font-extrabold text-[#161616] text-lg font-serif font-serif">Upload Sequencing Report</h3>
                <p className="text-xs text-[#6d6a63] mt-0.5">Order Ref: <span className="font-mono text-[#0f3e17] font-bold">{selectedOrderForUpload.id}</span></p>
              </div>
              <button onClick={() => { setSelectedOrderForUpload(null); setHasParsed(false); }} className="p-1.5 border border-[#e6e2d8] hover:border-[#6d6a63] rounded-full bg-white transition">
                <SvgClose />
              </button>
            </div>

            {!hasParsed ? (
              <div className="space-y-4 text-xs font-semibold text-center py-6">
                <div className="border-2 border-dashed border-[#e6e2d8] hover:border-[#0f3e17] p-8 rounded-2xl transition cursor-pointer bg-[#fdfdfc] flex flex-col items-center justify-center space-y-3">
                  <div className="p-3 bg-[#ece8df]/20 border border-[#e6e2d8] rounded-full text-[#0f3e17]">
                    <SvgDoc />
                  </div>
                  <div className="space-y-1">
                    <span className="block text-sm font-extrabold text-[#161616] font-serif">Upload Laboratory NGS PDF Report</span>
                    <span className="block text-[11px] text-[#6d6a63] font-semibold leading-relaxed">PDF records are automatically parsed for targetable gene fusions, variants, and copy-number alterations.</span>
                  </div>
                  <input type="file" accept=".pdf" onChange={handleFileUpload} className="hidden" id="pdfReportInput" />
                  <label htmlFor="pdfReportInput" className="py-2 px-4 bg-white border border-[#e6e2d8] text-[#161616] hover:border-[#0f3e17] font-bold text-xs rounded-xl cursor-pointer shadow-sm transition inline-block uppercase tracking-wider">
                    {isUploading ? 'Ingesting PDF data...' : 'Select PDF Document'}
                  </label>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-[#2f6f3e]/5 p-4 rounded-xl border border-[#2f6f3e]/15 text-xs text-[#2f6f3e] font-semibold">
                  ✓ Successfully extracted molecular variants! Please review mutations below before importing.
                </div>
                <div className="space-y-2 border border-[#e6e2d8] rounded-xl p-4 bg-[#fdfdfc] max-h-52 overflow-y-auto">
                  <strong className="block text-[10px] font-extrabold text-[#6d6a63] uppercase tracking-wider mb-2">Parsed Actionable Mutations:</strong>
                  {parsedVariants.length === 0 ? (
                    <p className="text-xs italic text-[#6d6a63]">No pathogenic tumor fusions or variants detected.</p>
                  ) : (
                    <div className="space-y-2 font-mono text-xs text-[#161616]">
                      {parsedVariants.map((v, idx) => (
                        <div key={idx} className="flex justify-between items-center p-2.5 bg-white border border-[#e6e2d8] rounded-xl">
                          <span className="font-bold text-[#0f3e17]">{v.gene} {v.variant_name}</span>
                          <span className="text-[10px] font-semibold text-[#6d6a63]">VAF: {v.vaf_percentage || '45'}%</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-3 border-t border-[#e6e2d8] pt-4">
                  <button onClick={() => { setSelectedOrderForUpload(null); setHasParsed(false); }} className="py-2.5 px-4 border border-[#e6e2d8] hover:bg-[#ece8df]/40 text-xs font-bold text-[#6d6a63] rounded-xl transition">
                    Cancel
                  </button>
                  <button onClick={handleVerifyAndSave} className="py-2.5 px-5 bg-[#0f3e17] text-white hover:bg-[#1a4a21] text-xs font-bold rounded-xl transition border border-[#0f3e17] uppercase tracking-wider shadow-sm">
                    Verify & Link to Profile
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 3. Book Genetic Counselors Modal */}
      {isBookingCounselor && (
        <div className="fixed inset-0 z-50 bg-[#161616]/65 backdrop-blur-sm flex justify-center items-center p-4">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl p-6 border border-[#e6e2d8] space-y-5 animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center border-b border-[#e6e2d8] pb-3 text-xs font-semibold">
              <div>
                <h3 className="font-extrabold text-[#161616] text-lg font-serif font-serif">Book Counseling Session</h3>
                <p className="text-xs text-[#6d6a63] mt-0.5">Counselor: <span className="text-[#0f3e17] font-bold">{isBookingCounselor.name}</span></p>
              </div>
              <button onClick={() => { setIsBookingCounselor(null); setBookingSuccess(false); setBookingDate(''); }} className="p-1.5 border border-[#e6e2d8] hover:border-[#6d6a63] rounded-full bg-white transition">
                <SvgClose />
              </button>
            </div>

            {bookingSuccess ? (
              <div className="bg-[#2f6f3e]/10 border border-[#2f6f3e]/20 text-[#2f6f3e] p-5 rounded-2xl text-xs font-semibold text-center space-y-1.5">
                <div className="text-xl">✅</div>
                <strong className="block text-sm font-extrabold font-serif">Session Scheduled successfully!</strong>
                <p className="text-[11px] leading-relaxed text-[#6d6a63]">Confirmation details and secure Zoom invitation have been dispatched to your email.</p>
              </div>
            ) : (
              <div className="space-y-4 text-xs font-semibold">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold text-[#6d6a63] uppercase tracking-wider">Select Consultation Date & Time</label>
                  <input type="datetime-local" value={bookingDate} onChange={(e) => setBookingDate(e.target.value)} className="w-full text-xs p-3 border border-[#e6e2d8] rounded-xl bg-white font-semibold" min={new Date().toISOString().slice(0, 16)} />
                </div>

                <div className="bg-[#ece8df]/20 border border-[#e6e2d8] rounded-xl p-4 text-[11px] text-[#6d6a63] leading-relaxed space-y-1 font-medium">
                  <strong className="text-[#161616] block mb-1">Standard Telehealth Policies:</strong>
                  <div>• Zoom invitation details will arrive via email 24 hours prior.</div>
                  <div>• Session changes or cancellations are permitted up to 12 hours before.</div>
                </div>

                <div className="flex justify-end gap-3 border-t border-[#e6e2d8] pt-4">
                  <button onClick={() => { setIsBookingCounselor(null); setBookingDate(''); }} className="py-2.5 px-4 border border-[#e6e2d8] hover:bg-[#ece8df]/40 text-xs font-bold text-[#6d6a63] rounded-xl transition">
                    Cancel
                  </button>
                  <button onClick={handleBookCounselor} className="py-2.5 px-5 bg-[#0f3e17] text-white hover:bg-[#1a4a21] text-xs font-bold rounded-xl transition border border-[#0f3e17] uppercase tracking-wider shadow-sm">
                    Book Session
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
